/** @jsxImportSource preact */
import { h } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { IncidentDetailContent, type IncidentDetail } from './IncidentDetailModal';
import { NatSecDetailContent } from './NatSecIncidentDetailModal';

export interface IncidentDetailInlineProps {
  apiBase: string;
  incidentId: string;
  /** Incidents dataset (API `src` param), e.g. 'incidents-arcola'. */
  dataset?: string;
  /**
   * CSS selector for the host page's server-rendered version of the same
   * content (the Webflow CMS rich text). Hidden only once the incident has
   * loaded, so a failed fetch or disabled JavaScript leaves it in place.
   */
  fallback?: string;
}

// Same rule as the Webflow sync's `has-natsec`: an assessment exists when
// the overall score is a number.
function hasNatSec(incident: IncidentDetail): boolean {
  const v = incident.natSecOverallScore;
  return v != null && v !== '' && Number.isFinite(Number(v));
}

/**
 * The incident detail body rendered inline on a per-incident page
 * (airisk.mit.edu/ai-incidents/<slug>), using the same component as the
 * Incident View modal, followed by the National Security assessment when
 * the incident has one. Renders nothing until the incident has loaded.
 */
export function IncidentDetailInline({ apiBase, incidentId, dataset, fallback }: IncidentDetailInlineProps) {
  const [incident, setIncident] = useState<IncidentDetail | null>(null);

  useEffect(() => {
    if (!/^\d+$/.test(incidentId)) {
      console.error(`IncidentDetailInline: invalid incidentId ${JSON.stringify(incidentId)}`);
      return;
    }
    let cancelled = false;
    const qs = new URLSearchParams({ chartType: 'incident-detail', incidentId });
    if (dataset) qs.set('src', dataset);
    fetch(`${apiBase}/data/incidents?${qs}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((json: { incident?: IncidentDetail | null }) => {
        if (!cancelled && json?.incident) setIncident(json.incident);
      })
      .catch((err) => console.error('IncidentDetailInline: failed to load incident', err));
    return () => { cancelled = true; };
  }, [apiBase, incidentId, dataset]);

  useEffect(() => {
    if (!incident || !fallback) return;
    document.querySelectorAll<HTMLElement>(fallback).forEach((el) => { el.style.display = 'none'; });
  }, [incident, fallback]);

  if (!incident) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <IncidentDetailContent incidentId={incidentId} incident={incident} loading={false} hideTitle />
      {hasNatSec(incident) && (
        <div>
          <h2 style={{ margin: '0 0 16px', fontSize: 22, fontWeight: 700, lineHeight: 1.3, color: 'var(--gc-foreground, inherit)' }}>
            National Security Assessment
          </h2>
          <NatSecDetailContent incidentId={incidentId} incident={incident} showLinks={false} />
        </div>
      )}
    </div>
  );
}
